import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { REGULATED_MARKET_LISTINGS, RegulatedMarketListing } from '@/constants/institution';
import { InstitutionFieldDocument } from '@/features/institution/components/fields/document';
import { InstitutionFieldsListedCompany } from '@/features/institution/components/fields/listed-company';
import { type InstitutionOwnershipOrDefaults, isListedOwnership } from '@/features/institution/ownership/schema';
import { DocumentSource } from '@/services/api/documents';

const InstitutionOwnershipFieldsetListing = () => {
  const form = useFormContext<InstitutionOwnershipOrDefaults>();
  const { i18n, t } = useTranslation();
  const regulatedMarketListing = form.watch('regulatedMarketListing');
  const isSubsidiary = regulatedMarketListing === RegulatedMarketListing.WHOLLY_OWNED_SUBSIDIARY;

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="regulatedMarketListing"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.ownership.listing.regulatedMarketListing')}*</FormLabel>
            <FormControl>
              <RadioGroup defaultValue={field.value} variant="cards" aria-label={t('institutionDetails.ownership.listing.regulatedMarketListing')} onValueChange={field.onChange}>
                {REGULATED_MARKET_LISTINGS.map((listing) => {
                  const optionDescription = `institutionDetails.ownership.listing.optionDescriptions.${listing}`;

                  return (
                    <FormItem key={listing}>
                      <RadioGroupItemCard variant="outline">
                        <FormControl>
                          <RadioGroupItem value={listing} />
                        </FormControl>
                        <FormLabel contained>{t(`institutionDetails.ownership.listing.options.${listing}`)}</FormLabel>
                        {i18n.exists(optionDescription) ? (
                          <FormDescription className="text-muted-foreground peer-aria-[checked=true]:text-primary">{t(optionDescription)}</FormDescription>
                        ) : null}
                      </RadioGroupItemCard>
                    </FormItem>
                  );
                })}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {isListedOwnership(regulatedMarketListing) ? (
        <fieldset className="space-y-6">
          <legend className="font-medium">{t('institutionDetails.ownership.listing.listedCompany', { context: isSubsidiary ? 'subsidiary' : undefined })}</legend>
          <InstitutionFieldsListedCompany isNameAsked={isSubsidiary} micDescription={t('institutionDetails.ownership.listing.micDescription')} />
          {isSubsidiary ? (
            <InstitutionFieldDocument<InstitutionOwnershipOrDefaults>
              description={t('institutionDetails.ownership.chart.description', { context: 'subsidiary' })}
              label={t('institutionDetails.ownership.chart.ownershipChart')}
              name="ownershipChart"
              source={DocumentSource.COMPANY_DOCUMENTS}
            />
          ) : null}
        </fieldset>
      ) : null}
    </div>
  );
};
InstitutionOwnershipFieldsetListing.displayName = 'InstitutionOwnershipFieldsetListing';

export { InstitutionOwnershipFieldsetListing };
